/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
/**
 * FrameIcon SVG component
 *
 * This icon represents a drawer layout state toggle. It visually indicates whether the right section
 * of a drawer (e.g., ContactDrawer) is collapsed or expanded:
 * - "narrow" variant: Shows a frame with a filled rectangle, representing the drawer with right section closed/collapsed
 * - "wide" variant: Shows a frame with a thin vertical line on the right, representing the drawer with right section open/expanded
 *
 * This icon supports two visual variants: "wide" (default) and "narrow".
 * For consistent sizing in the UI, both icon designs always use the same outer SVG coordinate space (`viewBox="0 0 24 24"`).
 *
 * - If variant is "wide", the icon is drawn using 24x24 coordinates.
 * - If variant is "narrow", the original narrow icon (designed for a 16x16 space) is centered inside the 24x24 area by wrapping shapes in a group
 *   with `transform="translate(4,4)"`. This effectively scales and aligns the two variants so they appear visually balanced and interchangeable.
 *
 * The group-wrapping technique ensures both icon variants share the same visual footprint, regardless of their original design space.
 *
 * @param props - Standard SVG props plus `variant` to select icon style ("narrow" | "wide")
 */
export default function FrameIcon(props: React.SVGProps<SVGSVGElement> & { variant?: `narrow` | `wide` }) {
  return (
    <svg
      width={props.width ?? 24}
      height={props.height ?? 24}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <g>
        {props.variant === `narrow` ? (
          <g transform="translate(4,4)">
            <path
              d="M12.6667 14H3.33333C2.59695 14 2 13.4031 2 12.6667V3.33333C2 2.59695 2.59695 2 3.33333 2H12.6667C13.4031 2 14 2.59695 14 3.33333V12.6667C14 13.4031 13.4031 14 12.6667 14Z"
              stroke="currentColor"
              strokeWidth="1.33"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <rect
              x="6"
              y="4"
              width="6"
              height="8"
              fill="currentColor"
              stroke="currentColor"
              strokeWidth="1.33"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        ) : (
          <>
            {/* Already natively 24x24 coordinates; no transform needed */}
            <path
              d="M16.6667 18H7.33333C6.59695 18 6 17.4031 6 16.6667V7.33333C6 6.59695 6.59695 6 7.33333 6H16.6667C17.4031 6 18 6.59695 18 7.33333V16.6667C18 17.4031 17.4031 18 16.6667 18Z"
              stroke="currentColor"
              strokeWidth="1.33"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <rect
              x="15"
              y="8"
              width="1"
              height="8"
              fill="currentColor"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </>
        )}
      </g>
    </svg>
  )
}
